<template>
  <div>
    <div v-if="isLoading" class="lesson-loader">
      <div class="loading-spinner">
        <div class="loading-circle"></div>
        <div class="loading-text">
          <div class="text-lg font-medium text-secondary-700">
            {{ headerText }}
          </div>
          <div class="text-sm font-medium text-tertiary">
            {{ randomSubHeaderText() }}
          </div>
        </div>
      </div>
    </div>
    <slot v-else></slot>
  </div>
</template>

<script>
import LessonsMixin from '@/mixins/lessons';

export default {
  name: 'LessonLoader',
  mixins: [LessonsMixin],
  emits: ['loaded'],
  data() {
    return {
      loadingFlashcardStates: false,
      subHeaderTextCache: null,
    };
  },
  computed: {
    lessonId() {
      return Number(this.$route.params.lessonId);
    },
    lesson() {
      if (!this.lessonId) {
        return null;
      }
      return this.lessons.find(lesson => lesson.id === this.lessonId);
    },
    isLoading() {
      return this.loadingLessons || this.loadingFlashcardStates;
    },
    isLessonComplete() {
      if (!this.lesson || !this.lesson.flashcardIds) {
        return false;
      }

      const lessonFlashcardStates = this.allLessonFlashcardStates.filter(
        state => state.lessonId === this.lesson.id,
      );

      // Check if all flashcards in the lesson have been answered
      return this.lesson.flashcardIds.every(flashcardId =>
        lessonFlashcardStates.some(state => state.flashcardId === flashcardId),
      );
    },
    headerText() {
      // based on the route, return the correct header text
      if (this.$route.name === 'lessons#show') {
        return 'Generating Lesson...';
      } else {
        return 'Loading Lesson...';
      }
    },
  },
  watch: {
    lesson: {
      immediate: true,
      handler(newLesson) {
        if (newLesson && !this.isLoading) {
          this.handleRouteLogic();
        }
      },
    },
    isLoading: {
      immediate: true,
      handler(isLoading, wasLoading) {
        if (wasLoading && !isLoading) {
          this.$emit('loaded');
        }
        if (!isLoading && this.lesson) {
          this.handleRouteLogic();
        }
      },
    },
    '$route': {
      handler() {
        if (this.lesson && !this.isLoading) {
          this.handleRouteLogic();
        }
      },
    },
  },
  created() {
    this.loadData();
  },
  methods: {
    async loadData() {
      // Load flashcard states
      this.loadingFlashcardStates = true;
      try {
        await this.$store.dispatch('flashcardStates/fetchStates', ['bookmarks', 'flashcards', 'stats']);
      } catch (error) {
        console.error('Error loading flashcard states:', error);
      } finally {
        this.loadingFlashcardStates = false;
      }
    },
    handleRouteLogic() {
      // Return early if lesson data is not yet available
      if (!this.lesson) {
        if (!this.loadingLessons) {
          console.error('Invalid lesson ID:', this.lessonId);
          this.$router.push({ name: 'home' });
        }
        return;
      }

      const currentRoute = this.$route.name;
      const currentFlashcardId = this.$route.params.flashcardId !== undefined ? Number(this.$route.params.flashcardId) : null;
      const currentIndex = currentFlashcardId !== null ? this.lesson.flashcardIds.indexOf(currentFlashcardId) : null;

      if (this.isLessonComplete) {
        if (currentRoute === 'lessons#show') {
          return this.$router.push({ name: 'lessons#results', params: { lessonId: this.lessonId } });
        }
        return;
      } else if (!this.isLessonComplete) {
        // Get the flashcard ID to navigate to, and validate it exists
        const targetFlashcardId = this.firstUnansweredFlashcardId;
        const firstUnansweredIndex = this.lesson.flashcardIds.indexOf(targetFlashcardId);

        // If there is no target flashcard, the lesson is actually complete
        if (!targetFlashcardId) {
          if (currentRoute !== 'lessons#results') {
            return this.$router.push({ name: 'lessons#results', params: { lessonId: this.lessonId } });
          }
          return;
        }

        if (currentRoute === 'lessons#results') {
          return this.$router.push({
            name: 'lessons#show',
            params: {
              lessonId: this.lessonId,
              flashcardId: targetFlashcardId,
            },
          });
        }
        if (currentRoute === 'lessons#show') {
          if (currentIndex === null || currentIndex === -1) {
            return this.$router.push({
              name: 'lessons#show',
              params: {
                lessonId: this.lessonId,
                flashcardId: targetFlashcardId,
              },
            });
          }

          if (currentIndex > firstUnansweredIndex) {
            return this.$router.push({
              name: 'lessons#show',
              params: {
                lessonId: this.lessonId,
                flashcardId: targetFlashcardId,
              },
            });
          }

          // We're on a valid flashcard at or before the first unanswered index
          return;
        } else if (currentRoute === 'lessons#question-review') {
          return this.$router.push({
            name: 'lessons#show',
            params: {
              lessonId: this.lessonId,
              flashcardId: targetFlashcardId,
            },
          });
        }
        return;
      }

      return;
    },
    randomSubHeaderText() {
      if (this.subHeaderTextCache) {
        return this.subHeaderTextCache;
      }
      this.subHeaderTextCache = this.getRandomSubHeaderText();
      setTimeout(() => {
        this.subHeaderTextCache = null;
      }, 2000);
      return this.subHeaderTextCache;
    },
    getRandomSubHeaderText() {
      const subHeaderTexts = [
        'Analyzing Performance',
        'Setting Difficulty',
        'Preparing Questions',
      ];
      return subHeaderTexts[Math.floor(Math.random() * subHeaderTexts.length)];
    },
  },
};
</script>

<style lang="scss">
.lesson-loader {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
}

.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.loading-circle {
  width: 40px;
  height: 40px;
  border: 3px solid #e0e0e0;
  border-top: 3px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-text {
  text-align: center;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
